<template>
  <form
    :id="formId"
    :data-testid="`lead-form-${source}`"
    novalidate
    :class="formClass"
    @submit="onSubmitEvent"
  >
    <div :class="rootClass">
      <FormField
        v-if="includes('name')"
        :id="`${formId}-name`"
        :label="t('form.fields.name')"
        :required="true"
        :error="mergeError('name')"
        :class="compact ? 'sm:flex-1' : ''"
      >
        <template #default="{ id, describedBy, invalid }">
          <BaseInput
            :id="id"
            v-model="state.name"
            type="text"
            autocomplete="name"
            :invalid="invalid"
            :described-by="describedBy"
            :disabled="submitting"
            @blur="r$.name.$touch()"
          />
        </template>
      </FormField>

      <FormField
        v-if="includes('phone')"
        :id="`${formId}-phone`"
        :label="t('form.fields.phone')"
        :required="phoneRequired"
        :optional="!phoneRequired"
        :error="mergeError('phone')"
        :class="compact ? 'sm:flex-1' : ''"
      >
        <template #default="{ id, describedBy, invalid }">
          <BaseInput
            :id="id"
            v-model="state.phone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            :placeholder="t('form.placeholder.phone')"
            :invalid="invalid"
            :described-by="describedBy"
            :disabled="submitting"
            @blur="r$.phone.$touch()"
          />
        </template>
      </FormField>

      <FormField
        v-if="includes('email')"
        :id="`${formId}-email`"
        :label="t('form.fields.email')"
        :required="emailRequired"
        :optional="!emailRequired"
        :error="mergeError('email')"
        :hide-label="compact && fields.length === 1"
        :class="compact ? 'sm:flex-1' : ''"
      >
        <template #default="{ id, describedBy, invalid }">
          <BaseInput
            :id="id"
            v-model="state.email"
            type="email"
            autocomplete="email"
            :placeholder="t('form.placeholder.email')"
            :invalid="invalid"
            :described-by="describedBy"
            :disabled="submitting"
            @blur="r$.email.$touch()"
          />
        </template>
      </FormField>

      <FormField
        v-if="includes('company')"
        :id="`${formId}-company`"
        :label="t('form.fields.company')"
        :optional="true"
        :error="mergeError('company')"
        :class="compact ? 'sm:flex-1' : ''"
      >
        <template #default="{ id, describedBy, invalid }">
          <BaseInput
            :id="id"
            v-model="state.company"
            type="text"
            autocomplete="organization"
            :placeholder="t('form.placeholder.company')"
            :invalid="invalid"
            :described-by="describedBy"
            :disabled="submitting"
            @blur="r$.company.$touch()"
          />
        </template>
      </FormField>

      <FormField
        v-if="includes('message')"
        :id="`${formId}-message`"
        :label="t('form.fields.message')"
        :optional="true"
        :error="mergeError('message')"
        :class="compact ? '' : 'md:col-span-2'"
      >
        <template #default="{ id, describedBy, invalid }">
          <BaseTextarea
            :id="id"
            v-model="state.message"
            :rows="4"
            :placeholder="t('form.fields.messagePlaceholder')"
            :invalid="invalid"
            :described-by="describedBy"
            :disabled="submitting"
            @blur="r$.message.$touch()"
          />
        </template>
      </FormField>
    </div>

    <BaseCheckbox
      :id="`${formId}-consent`"
      :checked="state.consent"
      :invalid="Boolean(mergeError('consent'))"
      :described-by="mergeError('consent') ? `${formId}-consent-err` : undefined"
      :disabled="submitting"
      size="sm"
      @update:checked="state.consent = $event"
    >
      {{ t('form.consent.label') }}
    </BaseCheckbox>
    <p
      v-if="mergeError('consent')"
      :id="`${formId}-consent-err`"
      class="text-xs font-medium text-danger-700"
      role="alert"
    >
      {{ mergeError('consent') }}
    </p>

    <div :class="footerClass">
      <p v-if="!compact" class="text-xs text-ink-muted">{{ t('form.httpsNote') }}</p>
      <BaseButton
        type="submit"
        variant="primary"
        size="md"
        :loading="submitting"
        :block="compact"
        :data-testid="`lead-form-${source}-submit`"
      >
        {{ submitting ? t('form.submitting') : effectiveSubmitLabel }}
      </BaseButton>
    </div>
  </form>
</template>

<script setup lang="ts">
/**
 * `LeadForm` — универсальная лид-форма (view-слой).
 *
 * Компонент отвечает только за разметку полей и привязку локального
 * состояния. Валидация — через Regle (`@regle/core`, глобальная
 * конфигурация — `src/lib/validation/regle.ts`). Сайд-эффекты вынесены:
 *   - `lib/forms/buildLeadPayload` — собирает `LeadPayload`;
 *   - `lib/forms/readRequestMeta` — читает UTM/referrer;
 *   - `composables/useLeadSubmit` — submit/toast/focus-first-error/reset.
 *
 * Подключается в: `HomeContact`, `Footer.newsletter`, `LeadMagnet`,
 * `PricingCalculator`, `QuizBrief`.
 *
 * Спека: `design/03-design-system.md § 5.3/5.4`, тексты ошибок —
 * `content/13-ui-microcopy.md § Валидация форм`.
 */
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { cva } from 'class-variance-authority';
import { useRegle } from '@regle/core';
import { email, maxLength, required } from '@regle/rules';
import { isTrue, phone } from '@/lib/validation/regle';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseTextarea from '@/components/base/BaseTextarea.vue';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
import FormField from '@/components/form/FormField.vue';
import { useLocale } from '@/composables/useLocale';
import { useLeadSubmit, type LeadFormErrorKey } from '@/composables/useLeadSubmit';
import { getSubmitter } from '@/lib/submitter';
import type { FormSubmitter, LeadSource, SubmitResult } from '@/lib/submitter';
import {
  buildLeadPayload,
  type LeadFormFieldKey,
  type LeadFormValues,
} from '@/lib/forms/buildLeadPayload';

const formVariants = cva('flex flex-col', {
  variants: {
    compact: {
      true: 'gap-3',
      false: 'gap-4',
    },
  },
  defaultVariants: { compact: false },
});

const rootVariants = cva('', {
  variants: {
    compact: {
      true: 'flex flex-col gap-3 sm:flex-row sm:items-start',
      false: 'grid gap-4 md:grid-cols-2',
    },
  },
  defaultVariants: { compact: false },
});

const footerVariants = cva('', {
  variants: {
    compact: {
      true: 'flex flex-col items-stretch gap-2 sm:flex-row sm:items-center',
      false: 'flex flex-wrap items-center justify-between gap-3',
    },
  },
  defaultVariants: { compact: false },
});

export type { LeadFormFieldKey } from '@/lib/forms/buildLeadPayload';
export type LeadFormPrefill = Partial<Record<LeadFormFieldKey, string>>;

interface Props {
  source: LeadSource;
  fields?: readonly LeadFormFieldKey[];
  submitLabel?: string | null;
  compact?: boolean;
  extras?: Record<string, unknown> | null;
  /**
   * Стартовые значения полей. Применяются только при монтировании —
   * последующие изменения пропа игнорируются, чтобы не затирать ввод
   * пользователя.
   */
  prefill?: LeadFormPrefill | null;
  /**
   * Адаптер для теста / оверрайда. По умолчанию — singleton из
   * `getSubmitter()`.
   */
  submitter?: FormSubmitter | null;
}

const props = withDefaults(defineProps<Props>(), {
  fields: () => ['name', 'phone', 'email'] as const,
  submitLabel: null,
  compact: false,
  extras: null,
  prefill: null,
  submitter: null,
});

const emit = defineEmits<{
  (
    e: 'submitted',
    payload: { result: SubmitResult; fields: LeadFormValues; keys: readonly LeadFormFieldKey[] },
  ): void;
}>();

const { t } = useI18n();
const route = useRoute();
const { locale } = useLocale();

const submitterRef = computed<FormSubmitter>(() => props.submitter ?? getSubmitter());

const state = reactive({
  name: props.prefill?.name ?? '',
  email: props.prefill?.email ?? '',
  phone: props.prefill?.phone ?? '',
  company: props.prefill?.company ?? '',
  message: props.prefill?.message ?? '',
  consent: false,
});

const fieldKeys = computed<readonly LeadFormFieldKey[]>(() => props.fields);
const includes = (key: LeadFormFieldKey): boolean => fieldKeys.value.includes(key);

const emailRequired = computed(() => includes('email'));
const phoneRequired = computed(() => !includes('email') && includes('phone'));

const rules = computed(() => ({
  name: includes('name') ? { required, maxLength: maxLength(120) } : {},
  email: includes('email') ? { required, email, maxLength: maxLength(160) } : {},
  phone: includes('phone')
    ? phoneRequired.value
      ? { required, phone, maxLength: maxLength(40) }
      : { phone, maxLength: maxLength(40) }
    : {},
  company: includes('company') ? { maxLength: maxLength(160) } : {},
  message: includes('message') ? { maxLength: maxLength(1000) } : {},
  consent: { isTrue },
}));

const { r$ } = useRegle(state, rules);

const ERROR_KEYS: readonly LeadFormErrorKey[] = [
  'name',
  'email',
  'phone',
  'company',
  'message',
  'consent',
];

function regleError(key: LeadFormErrorKey): string | undefined {
  const field = r$[key];
  if (!field || !field.$error) return undefined;
  return field.$errors[0];
}

function mergeError(key: LeadFormErrorKey): string | undefined {
  return serverFieldErrors.value[key] ?? regleError(key);
}

function currentValues(): LeadFormValues {
  return {
    name: state.name,
    email: state.email,
    phone: state.phone,
    company: state.company,
    message: state.message,
    consent: state.consent,
  };
}

function fieldElementId(key: LeadFormErrorKey): string {
  return `lead-${props.source.replace(/\./g, '-')}-${String(key)}`;
}

function resetForm(): void {
  state.name = '';
  state.email = '';
  state.phone = '';
  state.company = '';
  state.message = '';
  state.consent = false;
  serverFieldErrors.value = {};
  r$.$reset();
}

async function validate(): Promise<boolean> {
  const { valid } = await r$.$validate();
  return valid;
}

function firstErrorField(): LeadFormErrorKey | null {
  for (const key of ERROR_KEYS) {
    const field = r$[key];
    if (field && field.$error) return key;
  }
  return null;
}

const { submitting, serverFieldErrors, submit } = useLeadSubmit({
  submitter: submitterRef,
  validate,
  firstErrorField,
  buildPayload: () =>
    buildLeadPayload({
      source: props.source,
      locale: locale.value,
      fields: fieldKeys.value,
      values: currentValues(),
      path: route.fullPath,
      extras: props.extras,
    }),
  resetForm,
  fieldElementId,
  onSubmitted: (result) => {
    emit('submitted', { result: result, fields: currentValues(), keys: fieldKeys.value });
  },
});

const effectiveSubmitLabel = computed(() => props.submitLabel ?? t('form.submit'));

async function onSubmitEvent(event: Event): Promise<void> {
  event.preventDefault();
  await submit();
}

const sourceSlug = computed(() => props.source.replace(/\./g, '-'));
const formId = computed(() => `lead-${sourceSlug.value}`);
const formClass = computed(() => formVariants({ compact: props.compact }));
const rootClass = computed(() => rootVariants({ compact: props.compact }));
const footerClass = computed(() => footerVariants({ compact: props.compact }));

defineExpose({
  submit,
  reset: resetForm,
});
</script>
